import React, { useState, useEffect } from 'react';
import api, { authService } from '../services/api';
import { AuthContext, AuthResponse, RegistrationData } from './AuthContextType';

const AuthProviderComponent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [username, setUsername] = useState<string | null>(null);
  const [user, setUser] = useState<{ id: string; username: string; is_staff: boolean } | null>(null);

  // Check authentication status when the app loads
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await authService.checkAuth();
      setIsAuthenticated(response.isAuthenticated);
      setUsername(response.username);
      setUser(response.user);
    } catch (error) {
      console.error('Auth check failed:', error);
      setIsAuthenticated(false);
      setUsername(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username: string, password: string): Promise<AuthResponse> => {
    try {
      const response = await authService.login(username, password);
      setIsAuthenticated(true);
      setUsername(response.username);
      setUser(response.user);
      return response;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const register = async (data: RegistrationData): Promise<AuthResponse> => {
    return await authService.register(data);
  };

  const verifyEmail = async (token: string): Promise<void> => {
    await authService.verifyEmail(token);
  };

  const resendVerificationEmail = async (email: string): Promise<void> => {
    await authService.resendVerificationEmail(email);
  };

  const logout = async () => {
    try {
      await authService.logout();
      setIsAuthenticated(false);
      setUsername(null);
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear auth state even if API call fails
      setIsAuthenticated(false);
      setUsername(null);
      setUser(null);
    }
  };

  const updateProfile = async () => {
    try {
      const response = await api.get('/api/user-profile/');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to update profile:', error);
      throw error;
    }
  };

  const updateNotificationPreferences = async () => {
    try {
      await api.get('/api/user-preferences/');
    } catch (error) {
      console.error('Failed to update notification preferences:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      username, 
      user, 
      loading: isLoading, 
      login,
      register,
      verifyEmail,
      resendVerificationEmail,
      logout,
      updateProfile,
      updateNotificationPreferences
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const AuthProvider = AuthProviderComponent; 